import { Box, Button, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { TabActive } from "../../../../types";
import {
  BudgetType,
  CalcOption,
  CalcTypeText,
  versionInfo,
} from "../../../../types/budget/budget";
import BudgetSupplier from "../BudgetSupplier";
import BudgetTotals from "../BudgetTotals/BudgetTotals";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useBudget,
  useBudgetActions,
  useBudgetMeta,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import {
  getSupplierRowId,
  useVisibleSupplierRows,
} from "../hooks/useVisibleSupplierRows";
import { BudgetViewMode, useBudgetViewMode } from "../hooks/useBudgetViewMode";
import GroupedRows from "../BudgetGroup/GroupedRows";
import { useProject } from "../../../../contexts/project.context";
import { toast } from "react-toastify";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";
import ImportServicesModal from "../ImportServicesModal";
import BudgetToolbar from "../BudgetToolbar";
import {
  getCategoriesWithServices,
  mergeEnabledCategoryKeysFromServiceItemTypeList,
  withDraftSortOrder,
} from "../../../../utils/budget-import-helper";
import { CSSProperties, memo, useMemo, useState } from "react";
import { featureFlags } from "../../../../config/featureFlags";

// Renders the supplier rows for the active calc option. Pulled into its own
// component (and memoized) so that the broader `RenderBudgetInfo` — which
// still reads many fields via `useBudget()` — does not force a re-render
// of the entire supplier list whenever an unrelated field changes. This
// component subscribes to the narrowest slices it needs: the supplier list
// (Suppliers slice) and the active calc option (Meta slice).
export const SupplierRows = memo(function SupplierRows() {
  const { activeCalcOption, isBudgetDisabled, activeBudgetId } =
    useBudgetMeta();
  const { activeBudgetDispatch, runBudgetAutosave } = useBudgetActions();

  // Exige 8px de movimento antes de considerar drag. É o que impede que um
  // clique no autocomplete de fornecedor ou nos botões da linha vire arrasto.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Mesmo hook consumido pela visão agrupada: as duas visões precisam
  // enxergar exatamente as mesmas linhas, na mesma ordem.
  const visibleRows = useVisibleSupplierRows();

  const sortableIds = useMemo(
    () =>
      visibleRows.map(({ el, absoluteIndex }) =>
        getSupplierRowId(el, absoluteIndex)
      ),
    [visibleRows]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    // Soltou fora de um alvo válido (ou no mesmo lugar): nada muda.
    if (!over || active.id === over.id) return;

    const from = visibleRows.find(
      ({ el, absoluteIndex }) =>
        getSupplierRowId(el, absoluteIndex) === active.id
    );
    const to = visibleRows.find(
      ({ el, absoluteIndex }) => getSupplierRowId(el, absoluteIndex) === over.id
    );
    if (!from || !to) return;

    activeBudgetDispatch({
      type: "REORDER_SUPPLIERS",
      payload: {
        oldIndex: from.absoluteIndex,
        newIndex: to.absoluteIndex,
      },
    });

    // O dispatch sozinho não faz a troca de aba dar flush (ver comentário em
    // BudgetItem/handleDragEnd). Sem isto a nova ordem se perderia ao sair da
    // aba antes do autosave de intervalo.
    if (activeBudgetId) {
      window.setTimeout(() => {
        void runBudgetAutosave("manual");
      }, 0);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableIds}
        strategy={verticalListSortingStrategy}
      >
        {visibleRows.map(({ el, absoluteIndex }, visibleIndex) => (
          <BudgetSupplier
            supplierId={el.supplierId}
            // `uid` é estável por LINHA: não muda ao reordenar nem ao trocar o
            // fornecedor selecionado. Com o índice no key (como era antes), toda
            // reordenação remontava a linha e derrubava o estado dos acordeões
            // de categoria.
            key={getSupplierRowId(el, absoluteIndex)}
            sortableId={getSupplierRowId(el, absoluteIndex)}
            index={absoluteIndex}
            displayIndex={visibleIndex}
            calcOption={activeCalcOption}
            dragDisabled={isBudgetDisabled}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
});

export const pillBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 28,
  padding: "0 14px",
  borderRadius: 14,
  fontSize: 13,
  fontWeight: 500,
  border: "0.5px solid #ccc",
  color: "#666",
  background: "#f5f5f5",
  transition: "all 0.15s",
  userSelect: "none",
  cursor: "pointer",
};

export const pillStyles: Record<string, CSSProperties> = {
  activeLog: {
    background: "#E6F1FB",
    color: "#0C447C",
    borderColor: "#378ADD",
  },
  activePro: {
    background: "#CECBF6",
    color: "#26215C",
    borderColor: "#534AB7",
  },
  disabled: { cursor: "default", opacity: 0.6, pointerEvents: "none" },
};

export function TypePill({
  label,
  active,
  activeStyle,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  activeStyle: CSSProperties;
  disabled: boolean;
  onClick: () => void;
}) {
  const style: CSSProperties = {
    ...pillBase,
    ...(active ? activeStyle : {}),
    ...(disabled ? pillStyles.disabled : {}),
  };

  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={active}
      aria-disabled={disabled}
      style={style}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {label}
    </span>
  );
}

/**
 * Alternador "Visão: Por Fornecedor / Por Agrupamento".
 *
 * Usa o mesmo `TypePill` das pílulas de tipo do orçamento — nenhum componente
 * novo. O padrão é "Por Fornecedor": orçamento existente abre exatamente como
 * abria antes do agrupamento.
 */
export function BudgetViewToggle({
  mode,
  onChange,
}: {
  mode: BudgetViewMode;
  onChange: (mode: BudgetViewMode) => void;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
      <span style={{ fontSize: 13, color: "#666" }}>Visão</span>
      <TypePill
        label="Por Fornecedor"
        active={mode === "supplier"}
        activeStyle={pillStyles.activeLog}
        disabled={false}
        onClick={() => onChange("supplier")}
      />
      <TypePill
        label="Por Agrupamento"
        active={mode === "group"}
        activeStyle={pillStyles.activeLog}
        disabled={false}
        onClick={() => onChange("group")}
      />
    </Box>
  );
}

export function hasConsultingBefore(
  list: versionInfo[],
  version: number
): boolean {
  const index = list.findIndex((item) => item.version === version);

  if (index === -1) return false;

  return list.slice(0, index).some((item) => item.isConsulting === true);
}

export function RenderBudgetInfo() {
  const {
    activeBudget,
    budgetList,
    loadingBudget,
    createEmptyBudget,
    activeBudgetDispatch,
    activeCalcOption,
    createBudget,
    isBudgetDisabled,
  } = useBudget();
  const { activeService } = useServices();
  const { tabActive } = useProject();

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [viewMode, setViewMode] = useBudgetViewMode();

  const hasEnabledCategories =
    Object.keys(
      mergeEnabledCategoryKeysFromServiceItemTypeList(
        activeService?.itemTypeList
      )
    ).length > 0;

  const hasOpenBudget = budgetList?.find((el) => el.status !== "DELETED");
  const activeBudgetIndex =
    budgetList?.findIndex((el) => el._id === activeBudget._id) || 0;

  if (!!loadingBudget) return <>carregando</>;

  const handleCreateBudget = () => {
    // Sempre abre o modal customizado
    setImportModalOpen(true);
  };

  const handleImportWithMapping = (
    _mapping: Map<string, CalcOption>,
    mode: "services" | "categories_only",
    type: BudgetType
  ) => {
    if (type === BudgetType.LOGISTICA) {
      createEmptyBudget(type);
      setImportModalOpen(false);
      return;
    }

    if (!activeService || !activeService._id) {
      toast.warn("Nenhum serviço encontrado para importar");
      return;
    }

    const { _id, name, budgetServices, ...serviceDataWithoutId } =
      activeService;
    const categoriesOnly = mode === "categories_only";

    createBudget({
      ...serviceDataWithoutId,
      budgetServices: categoriesOnly ? [] : withDraftSortOrder(budgetServices),
      ...(categoriesOnly
        ? {
            addedItemTypeList: [],
            supplierList: [],
            baseValues: [],
            mainAppliedTaxes: [],
            files: [],
            paymentSolicitation: [],
          }
        : {}),
      type,
      status: "OPEN" as const,
      versionHistory: [{ status: "OPEN", version: 0, comparisonVersion: 0 }],
      budgetIndex: 0,
    });
    setImportModalOpen(false);
  };

  const handleCreateEmpty = (type: BudgetType) => {
    // Criar orçamento vazio (sem importar nada)
    createEmptyBudget(type);
    setImportModalOpen(false);
  };

  const mainContent =
    !budgetList ||
    budgetList.length < 1 ||
    !hasOpenBudget ||
    activeBudgetIndex === -1 ||
    (activeBudget?.versionHistory[0]?.status !== "OPEN" &&
      tabActive === TabActive.BUDGET) ? (
      <Box
        display={"flex"}
        margin={"0 auto"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <span style={{ fontSize: "20px", fontWeight: "semi-bold" }}>
          Crie ou Selecione um orçamento para começar
        </span>
        <Box gap={2} display={"flex"} justifyContent={"space-evenly"}>
          <Button
            onClick={handleCreateBudget}
            sx={{
              fontSize: "14px",
              mt: 2,
              p: 1,
            }}
          >
            Novo orçamento
          </Button>
        </Box>
      </Box>
    ) : activeCalcOption !== "budgetTotals" ? (
      <Box width={"100%"}>
        <Box pb={2}>
          <Box gap={2} p={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <span>{CalcTypeText[activeCalcOption]}</span>
              <BudgetToolbar />
            </Box>
            <Divider sx={{ p: 1 }} />
            {/*
              O alternador é a ÚNICA porta de entrada do agrupamento: sem ele
              não há como chegar na visão agrupada, e `useBudgetViewMode` já
              prende o modo em "supplier" quando a flag está desligada.
            */}
            {featureFlags.budgetGroups && (
              <BudgetViewToggle mode={viewMode} onChange={setViewMode} />
            )}
          </Box>
        </Box>
        {viewMode === "group" ? <GroupedRows /> : <SupplierRows />}
        {/*
          O "Novo fornecedor" de largura total é da visão PLANA: lá ele cria a
          linha de fornecedor vazia. Na visão agrupada a criação é por
          agrupamento ("+ Adicionar fornecedor em …") e a lista termina em
          "+ Novo agrupamento", como no protótipo.
        */}
        <Box
          sx={{
            display: viewMode === "group" ? "none" : "flex",
            flexDirection: "column",
            width: "101.6%",
            justifyContent: "center",
            alignItems: "stretch",
            marginTop: "5px",
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              activeBudgetDispatch({
                type: "ADD_SUPPLIER",
                payload: { calcOption: activeCalcOption },
              });
            }}
            startIcon={<AddIcon />}
            sx={{
              fontSize: "14px",
              mt: 2,
              px: 1,
              width: "100%",
              boxSizing: "border-box",
              justifyContent: "center",
              alignItems: "center",
              color: "#1361A4",
              backgroundColor: "#e3f2fd",
              border: "1px solid #1361A4",
              "&:hover": {
                backgroundColor: "#d0e7fb",
                border: "1px solid #1361A4",
              },
              "& .MuiButton-startIcon": {
                margin: 0,
                mr: 0.5,
              },
            }}
            variant="outlined"
            size="small"
            disabled={isBudgetDisabled}
          >
            Novo fornecedor
          </Button>
        </Box>
      </Box>
    ) : (
      <BudgetTotals />
    );

  return (
    <>
      {mainContent}
      <ImportServicesModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImportWithMapping}
        onCreateEmpty={handleCreateEmpty}
        categoriesWithServices={getCategoriesWithServices(activeService)}
        hasEnabledCategories={hasEnabledCategories}
        hasDraftItems={(activeService?.budgetServices?.length || 0) > 0}
      />
    </>
  );
}
